import { useCallback, useState } from 'react'
import type { Attachment } from 'types/attachments'
import type { MutationResult, QueryResult } from 'utils/api'
import { qs } from 'utils/api'

import { useApiQuery } from '../useApiQuery'
import { useFetchApi } from '../useFetchApi'

export interface AttachmentsParams {
  skip?: number
  take?: number
}

export function useAttachments(params?: AttachmentsParams): QueryResult<Attachment[]> {
  return useApiQuery<Attachment[]>(`/api/attachments${qs(params)}`)
}

export function useUploadAttachment(): MutationResult<Attachment, [File]> {
  const { fetchApi } = useFetchApi()
  const [loading, setLoading] = useState(false)
  const perform = useCallback(
    async (file: File): Promise<Attachment> => {
      setLoading(true)
      try {
        const formData = new FormData()
        formData.append('file', file)
        return await fetchApi<Attachment>('/api/attachments/upload', {
          method: 'POST',
          body: formData,
        })
      } finally {
        setLoading(false)
      }
    },
    [fetchApi],
  )
  return { perform, loading }
}

export function useDeleteAttachment(): MutationResult<void, [string]> {
  const { fetchApi } = useFetchApi()
  const [loading, setLoading] = useState(false)
  const perform = useCallback(
    async (id: string): Promise<void> => {
      setLoading(true)
      try {
        await fetchApi<void>(`/api/attachments/${id}`, { method: 'DELETE' })
      } finally {
        setLoading(false)
      }
    },
    [fetchApi],
  )
  return { perform, loading }
}
